import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type PendingAction } from '../api'

/**
 * What is waiting on you — TECHNICAL_DESIGN.md §4.
 *
 * **Eight queues, no inbox.** Proposals wait on Awaiting approval, unreadable
 * shapes on Unmapped layouts, citations on Clause review, refusals on Refused
 * items. Each screen is the right place to do the work and none of them is a
 * place to find out there is any, so somebody holding four capabilities had four
 * screens to visit before they knew whether their morning was empty. The
 * failure that follows is quiet and expensive: a client's guidelines sit in a
 * queue nobody opened, because nothing anywhere said they had arrived.
 *
 * **At the right end of the bar, away from the sections.** The other menus
 * answer *where do I go*; this one answers *what is waiting*, which is a
 * different question and the one people arrive with. It carries the total on the
 * control itself — a badge you have to open to read is a badge that does not
 * work.
 *
 * **Only what you can act on.** The store gates every row on a capability the
 * signed-in principal holds, so an author never sees an approval count. A number
 * somebody cannot act on is not a task; it is a figure that makes the product
 * look busy and teaches them to stop reading the badge.
 *
 * Rows at zero are kept rather than hidden, so *nothing is waiting* and *this
 * queue is not yours* stay different things on the screen.
 */
/** How often the counts refresh on their own while the page is on screen. */
export const REFRESH_EVERY_MS = 2 * 60 * 1000

export function PendingActions({ onGo }: { onGo: (screen: string) => void }) {
  const [actions, setActions] = useState<PendingAction[] | null>(null)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  const refresh = useCallback(() => {
    api
      .pendingActions()
      .then((rows) => {
        setActions(rows)
        setError(false)
      })
      .catch(() => setError(true))
  }, [])

  // On mount for the badge, and again every time the menu is opened. Fetching
  // once was the same staleness the inbox exists to remove: a client's
  // guidelines arriving after the page loaded left the badge reading zero, and
  // a badge that is wrong is worse than no badge — the reader has stopped
  // checking the queues *because* of it.
  useEffect(refresh, [refresh])

  // And it keeps itself current: every two minutes while the page is on screen,
  // and as soon as somebody comes back to a tab they left. The menu used to say
  // "Counted when you opened this", which asked the reader to allow for a stale
  // number instead of the number simply not being stale.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh()
    }, REFRESH_EVERY_MS)
    const back = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', back)
    window.addEventListener('focus', back)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', back)
      window.removeEventListener('focus', back)
    }
  }, [refresh])

  useEffect(() => {
    function away(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])

  const waiting = (actions ?? []).filter((a) => a.count > 0)
  const total = waiting.reduce((sum, a) => sum + a.count, 0)

  // Nothing to say and nothing to offer: somebody whose roles carry no queue at
  // all — a viewer — gets no control rather than an empty one.
  //
  // A *failure* is not that case, and used to be treated as it. The control
  // removed itself when the count threw, so a 500 from the route looked
  // precisely like an empty inbox — which is how it shipped: the badge appeared
  // for the moment the request was in flight and then vanished. An inbox that
  // deletes itself when it cannot count is worse than one that says so.
  if (!error && actions !== null && actions.length === 0) return null

  return (
    <div className="menu pending" ref={box}>
      <button
        className="tab"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (!open) refresh()
          setOpen((o) => !o)
        }}
        title="Everything waiting on you, across every queue you can act on."
      >
        My pending actions
        {total > 0 && (
          <span className="pending-badge" data-testid="pending-total">
            {total}
          </span>
        )}
        <span className="chevron chevron-tab" aria-hidden />
      </button>
      {open && (
        <div className="menu-panel pending-panel" role="menu">
          {error ? (
            <p className="error" role="alert" data-testid="count-failed">
              Could not count what is waiting. The queues themselves are
              unaffected — open Unmapped layouts, Awaiting approval or Refused
              items directly.
            </p>
          ) : actions === null ? (
            <p className="note">Counting…</p>
          ) : total === 0 ? (
            <p className="done" data-testid="nothing-waiting">
              Nothing is waiting on you. Every queue you can act on is empty.
            </p>
          ) : (
            waiting.map((action) => (
              <button
                key={action.key}
                role="menuitem"
                className="menu-item"
                data-testid={`pending-${action.key}`}
                onClick={() => {
                  setOpen(false)
                  onGo(action.screen)
                }}
              >
                <span className="menu-item-label">
                  {action.label}
                  <span className="pending-count">{action.count}</span>
                </span>
                <span className="menu-item-blurb">{action.blurb}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
